import React from "react";
import { Activity, Clock3, SignalHigh } from "lucide-react";

import { cn, formatRelativeTime } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";

const barTone = (percent) => {
  if (percent >= 50) {
    return "bg-emerald-400";
  }

  if (percent >= 20) {
    return "bg-amber-300";
  }

  return "bg-zinc-400";
};

const MetricCard = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <Icon className="h-4 w-4 text-zinc-400" />
    </div>
    <p className="mt-4 font-mono text-2xl text-zinc-50">{value}</p>
    <p className="mt-1 text-sm font-['Manrope'] text-zinc-500">{detail}</p>
  </div>
);

const DistributionList = ({ title, items, emptyLabel }) => (
  <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">{title}</p>
    <div className="mt-4 space-y-3">
      {items.length > 0 ? (
        items.map((item) => (
          <div key={item.key} className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-['Manrope'] text-zinc-300">{item.label}</p>
              <p className="font-mono text-sm text-zinc-50">{item.count.toLocaleString()}</p>
            </div>
            <div className="h-2 rounded-full bg-zinc-800">
              <div
                className={cn("h-2 rounded-full transition-all duration-300", barTone(item.coveragePercent))}
                style={{ width: item.coveragePercent > 0 ? `${Math.max(item.coveragePercent, 3)}%` : "0%" }}
              />
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm font-['Manrope'] text-zinc-500">{emptyLabel}</p>
      )}
    </div>
  </div>
);

const LoadingState = () => (
  <div className="space-y-4">
    <div className="grid gap-3 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
          <Skeleton className="h-3 w-20 bg-white/10" />
          <Skeleton className="mt-4 h-6 w-16 bg-white/10" />
          <Skeleton className="mt-2 h-3 w-24 bg-white/10" />
        </div>
      ))}
    </div>
    <div className="grid gap-4 xl:grid-cols-2">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
          <Skeleton className="h-3 w-24 bg-white/10" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 4 }).map((__, rowIndex) => (
              <div key={rowIndex}>
                <div className="flex items-center justify-between gap-3">
                  <Skeleton className="h-3 w-20 bg-white/10" />
                  <Skeleton className="h-3 w-10 bg-white/10" />
                </div>
                <Skeleton className="mt-2 h-2 w-full bg-white/10" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-10 text-center">
    <p className="font-['Outfit'] text-zinc-50">No recent signals yet</p>
    <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
      This panel will populate once recent signal or event telemetry begins flowing through the startup stream.
    </p>
  </div>
);

const EventSignalPanel = ({ signalMetrics, loading }) => {
  const recentSignalCount = signalMetrics?.recentSignalCount || 0;
  const startupsWithSignals = signalMetrics?.startupsWithSignals || 0;
  const latestSignalAt = signalMetrics?.latestSignalAt || null;
  const byType = signalMetrics?.byType || [];
  const freshness = signalMetrics?.freshness || [];

  return (
    <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-5 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Signal Stream</p>
          <h2 className="mt-2 font-['Outfit'] text-xl text-zinc-50">Event and freshness counts</h2>
        </div>
        <div className="rounded-full border border-zinc-800 bg-zinc-950/70 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-400">
          Recent signal window
        </div>
      </div>

      <p className="mt-3 text-sm font-['Manrope'] text-zinc-500">
        Recent signal mix and freshness buckets from the live startup signal stream.
      </p>

      <div className="mt-5">
        {loading ? (
          <LoadingState />
        ) : recentSignalCount > 0 || startupsWithSignals > 0 ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <MetricCard
                label="Recent signals"
                value={recentSignalCount.toLocaleString()}
                detail="Most recent cross-startup signal rows"
                icon={SignalHigh}
              />
              <MetricCard
                label="Startups touched"
                value={startupsWithSignals.toLocaleString()}
                detail="Sampled startup records with at least one signal"
                icon={Activity}
              />
              <MetricCard
                label="Freshest seen"
                value={latestSignalAt ? formatRelativeTime(latestSignalAt) : "No recent"}
                detail="Newest signal timestamp observed"
                icon={Clock3}
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <DistributionList
                title="Signal types"
                items={byType}
                emptyLabel="No typed signals have landed in the current window."
              />
              <DistributionList
                title="Freshness buckets"
                items={freshness}
                emptyLabel="Freshness counts will appear once timestamps are available."
              />
            </div>
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};

export default EventSignalPanel;
